import { AssignStatus } from "@/enum/list.enum";

import { CopywritingStatus } from "@/pages/Private/pages/Messages/components/GenerateBar/types";

import { AiSnippetStatus } from "@/pages/Private/pages/List/components/CampaignOverview/components/Lead/utils/getAiSnippetStatus";

import { ListPerson } from "../../schema/list";

/**
 * The four mutually-exclusive states the per-lead "Reach out" button can be in, in
 * priority order (see {@link getReachOutStatus}):
 *
 * - `reply`      — the lead has replied and a reply is outstanding (Assigned tab only). Renders a
 *                  permanent "Reply" affordance (never hover-gated).
 * - `generating` — AI drafts or AI snippets are currently being generated (spinner chip).
 * - `ready`      — AI drafts or AI snippets are ready to review ("Review" affordance / ready chip).
 * - `default`    — nothing generated yet; the primary "Reach out ▾" launcher (opens the menu).
 */
export type ReachOutState = "default" | "generating" | "ready" | "reply";

/** Which kind of AI output drives a `generating` / `ready` state — decides the chip label. */
export type ReachOutAiKind = "drafts" | "snippets";

export interface ReachOutStatus {
	state: ReachOutState;
	aiKind: ReachOutAiKind | null;
}

export interface ReachOutStatusInput {
	/** `row.assignStatus` — `REPLIES` drives the reply state (only ever set in the Assigned tab). */
	assignStatus?: AssignStatus | null;
	/** `row.copywritingStatus` — the AI-drafts (chat sequence) generation lifecycle. */
	copywritingStatus?: CopywritingStatus | null;
	/** `!!row.copywritingOutput` — a COMPLETED status without output is not actually reviewable. */
	hasCopywritingOutput?: boolean;
	/**
	 * Pre-resolved AI-snippet status for the lead's assigned campaign (see {@link getAiSnippetStatus}).
	 * `null` when the assigned campaign uses no AI snippets (or no campaign is assigned).
	 */
	aiSnippetStatus?: AiSnippetStatus | null;
}

/**
 * Maps a lead's persisted generation/reply fields to the single {@link ReachOutState} that drives
 * the Outreach column's button. Pure so it is trivially unit-testable; the cell resolves the inputs
 * (notably `aiSnippetStatus`, which needs the campaign config) and passes them in.
 *
 * Snippets take precedence over chat drafts when both are present: a lead assigned to a snippet
 * campaign is on the campaign path, so its snippet lifecycle is the meaningful one.
 */
export const getReachOutStatus = ({
	assignStatus,
	copywritingStatus,
	hasCopywritingOutput = false,
	aiSnippetStatus = null,
}: ReachOutStatusInput): ReachOutStatus => {
	if (assignStatus === AssignStatus.REPLIES) {
		return { state: "reply", aiKind: null };
	}

	if (aiSnippetStatus === "creating") {
		return { state: "generating", aiKind: "snippets" };
	}

	if (
		copywritingStatus === CopywritingStatus.QUEUED ||
		copywritingStatus === CopywritingStatus.RUNNING
	) {
		return { state: "generating", aiKind: "drafts" };
	}

	if (aiSnippetStatus === "created") {
		return { state: "ready", aiKind: "snippets" };
	}

	if (copywritingStatus === CopywritingStatus.COMPLETED && hasCopywritingOutput) {
		return { state: "ready", aiKind: "drafts" };
	}

	return { state: "default", aiKind: null };
};

/**
 * Whether a list has at least one *valid* campaign — gates the "Add to campaign" and "Review & edit
 * campaign" paths in the reach-out menu (they are locked otherwise, with a "Create a campaign"
 * prompt). `campaignConfig` is a map keyed by campaign UUID plus a stray `name` key; filtering on
 * `isValid` naturally skips the latter.
 */
export const listHasValidCampaign = (list?: { campaignConfig?: unknown } | null): boolean => {
	const campaignConfig = (list?.campaignConfig ?? {}) as Record<string, { isValid?: boolean }>;

	return Object.values(campaignConfig).some((campaign) => campaign?.isValid === true);
};

/** {@link listHasValidCampaign} for a single lead's list. */
export const hasValidCampaign = (row: ListPerson): boolean => listHasValidCampaign(row.list);
